import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {OverlayTriggerType} from 'react-bootstrap/OverlayTrigger';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from '../publishing-qr-code-panel/publishing-qr-code-panel.module.scss';
import {Icon} from '@Components/icon-v2';
import {useWebLinks} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {PLAIN_TEXT_CLIPBOARD_MIMETYPE, PMW_CUSTOM_CLIPBOARD_MIMETYPE} from '@PosterWhiteboard/page/page-clipboard';
import {Text, TextSize} from '@Components/text';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';

const DELAY = 200;
const HIDE_TOOLTIP_DELAY_AFTER_CLICK = 2000;
const TOOLTIP_CLICK_TRIGGER: OverlayTriggerType = 'click';

export function PublishingCopyQrcode(): ReactElement {
  const webLink = useWebLinks();
  const [show, setShow] = useState<boolean>(false);
  const [message, setMessage] = useState<string>(window.i18next.t('pmwjs_qrcode_copied'));

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    if (show) {
      timeout = setTimeout(() => {
        setShow(false);
      }, HIDE_TOOLTIP_DELAY_AFTER_CLICK);
    }

    return (): void => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [show]);

  const copyQRCodeToClipboard = async (): Promise<void> => {
    trackGA4CustomEvent(GA4EventName.PUBLISHING_COPY_QR_CODE);
    try {
      const textBlob = new Blob([webLink.qrHostedShareableUrl], {type: PLAIN_TEXT_CLIPBOARD_MIMETYPE});
      const customBlob = new Blob([webLink.qrHostedShareableUrl], {type: PMW_CUSTOM_CLIPBOARD_MIMETYPE});
      const clipboardItem = new ClipboardItem({
        [textBlob.type]: textBlob,
        [customBlob.type]: customBlob,
      });
      await navigator.clipboard.write([clipboardItem]);
    } catch {
      setMessage(window.i18next.t('pmwjs_copy_qrcode_error'));
    }
    setShow(true);
  };

  return (
    <OverlayTrigger
      trigger={TOOLTIP_CLICK_TRIGGER}
      overlay={
        <Tooltip className={styles.tooltip}>
          <div className={'flex-row-align-center flex-justify-between'}>
            <Icon icon={'icon-check-fill'} size={IconSize.SIZE_ICON_16} className={styles.copiedIcon} />
            <Text val={message} size={TextSize.XSMALL} />
          </div>
        </Tooltip>
      }
      delay={{show: DELAY, hide: DELAY}}
      placement="top-end"
      show={show}
      onToggle={(): void => {
        void copyQRCodeToClipboard();
      }}
    >
      <Icon
        icon={'icon-copy'}
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.CIRCLE}
        type={IconType.SHADOW}
        className={styles.qrCopy}
        onClick={() => {
          void copyQRCodeToClipboard();
        }}
      />
    </OverlayTrigger>
  );
}
